import { useState, useEffect, useCallback } from "react";
import { Robot, Play, CircleNotch, CheckCircle, NotePencil, ArrowClockwise, WarningCircle, Plugs } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ACTION_META = {
  resolved: { label: "Löst", cls: "text-green-700 bg-green-50 border-green-200", icon: CheckCircle },
  drafted: { label: "Utkast skapat", cls: "text-blue-700 bg-blue-50 border-blue-200", icon: NotePencil },
  skipped: { label: "Hoppade över", cls: "text-zinc-500 bg-zinc-50 border-zinc-200", icon: ArrowClockwise },
  error: { label: "Fel", cls: "text-red-700 bg-red-50 border-red-200", icon: WarningCircle },
};

export default function AutopilotConsole({ agent, agentId }) {
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const connected = !!(agent?.fd_domain && agent?.fd_api_key);

  const loadLog = useCallback(async () => {
    try {
      const r = await api.get(`/agents/${agentId}/autopilot/log`);
      setLog(r.data.items || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [agentId]);

  useEffect(() => { loadLog(); }, [loadLog]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post(`/agents/${agentId}/autopilot/run`);
      if (r.data.ok) {
        toast.success(r.data.processed > 0 ? `Bearbetade ${r.data.processed} ärende(n).` : (r.data.note || "Inga nya ärenden att hantera just nu."));
        loadLog();
      } else {
        toast.error(r.data.error || "Kunde inte köra.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte köra autopiloten.");
    } finally { setRunning(false); }
  };

  const resolvedCount = log.filter((l) => l.action === "resolved").length;
  const draftedCount = log.filter((l) => l.action === "drafted").length;

  return (
    <div className="flex flex-col h-full bg-white" data-testid="autopilot-console">
      <div className="p-5 border-b border-zinc-100">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#0033FF]/10 text-[#0033FF] flex items-center justify-center">
            <Robot size={24} weight="bold" />
          </div>
          <div className="min-w-0">
            <p className="font-display font-bold truncate">{agent?.name || "Autopilot"}</p>
            <div className="flex items-center gap-1.5 text-xs">
              <span className={`h-2 w-2 rounded-full ${connected ? "bg-green-500" : "bg-zinc-300"}`} />
              <span className={connected ? "text-green-600 font-semibold" : "text-zinc-400"}>
                {connected ? "Ansluten till Freshdesk" : "Ej ansluten — fyll i Plattform-fliken"}
              </span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-4 text-center">
          <Stat label="Lösta" value={resolvedCount} />
          <Stat label="Utkast" value={draftedCount} />
          <Stat label="Läge" value={(agent?.autopilot_mode || "draft") === "auto" ? "Auto" : "Utkast"} small />
        </div>
        <button data-testid="autopilot-run-button" onClick={runNow} disabled={running || !connected}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#0033FF] text-white rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-[#0022CC] transition-colors disabled:opacity-50">
          {running ? <><CircleNotch size={16} className="animate-spin" /> Kör…</> : <><Play size={16} weight="fill" /> Kör nu (testa en runda)</>}
        </button>
        {!connected && (
          <p className="mt-2 text-xs text-zinc-400 flex items-center gap-1.5"><Plugs size={13} /> Anslut Freshdesk i Plattform-fliken och spara först.</p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-2 scroll-thin">
        <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Senaste aktivitet</p>
        {loading ? (
          <p className="text-center text-zinc-400 text-sm py-6">Laddar…</p>
        ) : log.length === 0 ? (
          <p className="text-center text-zinc-400 text-sm py-6">Ingen aktivitet ännu. Tryck "Kör nu" för att starta en runda.</p>
        ) : (
          log.map((l, i) => {
            const m = ACTION_META[l.action] || ACTION_META.skipped;
            const Icon = m.icon;
            return (
              <div key={i} className="flex items-start gap-2.5 rounded-lg border border-zinc-100 p-2.5" data-testid="autopilot-log-row">
                <span className={`shrink-0 mt-0.5 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold inline-flex items-center gap-1 ${m.cls}`}>
                  <Icon size={12} weight="bold" /> {m.label}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">#{l.ticket_id} {l.subject ? `· ${l.subject}` : ""}</p>
                  {l.reply && <p className="text-xs text-zinc-500 line-clamp-2">{l.reply}</p>}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, small }) {
  return (
    <div className="rounded-lg bg-zinc-50 border border-zinc-100 py-2">
      <p className={`font-display font-bold ${small ? "text-sm mt-0.5" : "text-xl"}`}>{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{label}</p>
    </div>
  );
}
